// Copyright (c) 2024, WSO2 LLC. (https://www.wso2.com). All Rights Reserved.
//
// This software is the property of WSO2 LLC. and its suppliers, if any.
// Dissemination of any information or reproduction of any material contained
// herein in any form is strictly forbidden, unless permitted by WSO2 expressly.
// You may not alter or remove any copyright or other notice from copies of this content.

import { Download } from '@mui/icons-material';
import { Box, Button, Grid, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Tooltip, Typography } from '@mui/material';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs, { Dayjs } from 'dayjs';
import DOMPurify from 'dompurify';
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';
import React, { useEffect, useState } from 'react';
import { Store } from 'react-notifications-component';
import { useParams } from 'react-router-dom';
import DividedCell from '../components/DividedCell';
import LinearLoading from '../components/LinearLoading';
import { RESOURCE_SERVER_URLS } from '../data/configs/spl-config';
import { useGetApi } from '../data/hooks/api';
import * as Types from '../data/utils/types';
import '../styles/SLAReport.css';
import { ErrorComponent } from './ErrorPage';
import { ItemNotFoundComponent } from './ItemNotFound';

type RouteParams = {
   sysId: string;
}

const SLAReport: React.FC = () => {

   const { sysId: id } = useParams<RouteParams>();
   const projectId = id ? DOMPurify.sanitize(id) : '';
   const [showReport, setShowReport] = useState(false);
   const [from, setFrom] = useState<string>(new Date().toLocaleDateString('en-CA'));
   const [to, setTo] = useState<string>(new Date().toLocaleDateString('en-CA'));

   const apiUrl = RESOURCE_SERVER_URLS[0] + `/generate-sla-report?projectSysId=${projectId}&from=${from}&to=${to}`;

   const { data, loading, error, getApiData } = useGetApi<Types.SLAReportResponse>(
      { url: apiUrl, headers: { "accept": "application/json" } }
   );

   const handleSubmit = () => {
      getApiData(apiUrl)
   }

   const handleFromChange = (newValue: Dayjs | null) => {
      if (newValue) {
         setFrom(newValue.format('YYYY-MM-DD'));
      }
   };

   const handleToChange = (newValue: Dayjs | null) => {
      if (newValue) {
         setTo(newValue.format('YYYY-MM-DD'));
      }
   };

   const handleRowClick = (rowData: any) => {
      if (rowData?.caseNumber !== "") {
         window.open(`/support/cases/${rowData.caseNumber}`, '_blank');
      } else {
         Store.addNotification({
            title: "Error!",
            message: "Case not found.",
            type: "danger",
            insert: "top",
            container: "top-right",
            animationIn: ["animate__animated", "animate__fadeIn"],
            animationOut: ["animate__animated", "animate__fadeOut"],
            dismiss: {
               duration: 5000,
               onScreen: true,
            },
         });
      }
   }

   useEffect(() => {
      if (data) {
         setShowReport(true);
      }
   }, [data]);

   const downloadPDF = () => {
      const input = document.getElementById('sla-report');
   
      if (input) {
         html2canvas(input, { scale: 2, logging: true }).then((canvas) => {
            const imgData = canvas.toDataURL('image/png', 1.0);
            const pdf = new jsPDF('p', 'mm', 'a4');
            const pdfWidth = pdf.internal.pageSize.getWidth();
            const pdfHeight = pdf.internal.pageSize.getHeight();
   
            const imgProps = pdf.getImageProperties(imgData);
            const imgHeight = (imgProps.height * pdfWidth) / imgProps.width;
            let heightLeft = imgHeight;
            let position = 0;
   
            pdf.addImage(imgData, 'PNG', 0, position, pdfWidth, imgHeight);
            heightLeft -= pdfHeight;
   
            while (heightLeft >= 0) {
               position = heightLeft - imgHeight;
               pdf.addPage();
               pdf.addImage(imgData, 'PNG', 0, position, pdfWidth, imgHeight);
               heightLeft -= pdfHeight;
            }
   
            pdf.save('SLA_Report.pdf');
         });
      }
   };

   const FormView = () => {
      return (
         <>
            <div className="backdrop" />
            <Paper className="centered-form">
               <Typography
                  gutterBottom
                  variant="h4"
                  component="div"
                  marginBottom={"20px"}
               >
                  SLA Report
               </Typography>

               <Typography variant="body1" gutterBottom marginBottom={"20px"}>
                  Cases that were opened during this period will be listed
               </Typography>

               <LocalizationProvider dateAdapter={AdapterDayjs}>
                  <Grid container spacing={2} marginBottom={"30px"}>
                     <Grid item>
                        <DatePicker
                           label="From"
                           value={dayjs(from)}
                           onChange={(newValue) => handleFromChange(newValue)}
                        />
                     </Grid>
                     <Grid item>
                        <DatePicker
                           label="To"
                           value={dayjs(to)}
                           onChange={(newValue) => handleToChange(newValue)}
                        />
                     </Grid>
                  </Grid>
               </LocalizationProvider>

               <Button variant="contained"
                  sx={{
                     color: 'white',
                     ':hover': {
                        bgcolor: '#e96900',
                        borderColor: 'primary.main',
                        color: 'white',
                     }
                  }}
                  onClick={handleSubmit}
               >
                  Submit
               </Button>
            </Paper>
         </>
      )
   }

   const MainView = () => {
      const colNameArray = ["WSO2 Case ID", "Case Type", "Priority", "State", "Opened", "Response", "Workaround", "Resolution"];
      const colAttributeArray = ["caseId", "caseType", "casePriority", "caseState", "opened", "response", "workaround", "resolution"];

      return (
         <>
            {showReport && (
               <Box
                  flex={2}
                  display="flex"
                  justifyContent="flex-end"
                  p={1}
                  marginBottom={'-80px'}
                  marginRight={'30px'}
                  sx={{
                     position: `${showReport ? 'fixed' : 'relative'}`,
                     top: '120px',
                     right: '30px',
                     zIndex: 2000
                  }}
               >
                  <Tooltip title="Download Report">
                     <Button variant="contained"
                        sx={{
                           color: 'white',
                           ':hover': {
                              bgcolor: '#e96900',
                              borderColor: 'primary.main',
                              color: 'white',
                           },
                           borderRadius: '7px'
                        }}
                        onClick={downloadPDF}
                     >
                        <Download />
                     </Button>
                  </Tooltip>
               </Box>

            )}

            <div id='sla-report'>
               <Box
                  width={"80%"}
                  padding={"3%"}
                  margin={"auto"}
                  sx={{
                     backgroundColor: "#f7f7f6",
                  }}
               >
                  <Box className='sla-header'>
                     <Typography variant="h4" className='sla-header-title'>
                        SLA Report - WSO2 Customer Service
                     </Typography>
                  </Box>

                  <Box id="sla-report" className="table-container table-info">
                     <TableContainer component={Paper}>
                        <Table size='small'>
                           <TableBody className='table-body'>
                              <TableRow>
                                 <TableCell><Typography variant='subtitle2' fontWeight={'bold'}>Project Name</Typography></TableCell>
                                 <TableCell>{data?.projectName}</TableCell>
                              </TableRow>
                              <TableRow>
                                 <TableCell><Typography variant='subtitle2' fontWeight={'bold'}>Project Key</Typography></TableCell>
                                 <TableCell>{data?.projectKey}</TableCell>
                              </TableRow>
                              <TableRow>
                                 <TableCell><Typography variant='subtitle2' fontWeight={'bold'}>From</Typography></TableCell>
                                 <TableCell>{from}</TableCell>
                              </TableRow>
                              <TableRow>
                                 <TableCell><Typography variant='subtitle2' fontWeight={'bold'}>To</Typography></TableCell>
                                 <TableCell>{to}</TableCell>
                              </TableRow>
                              <TableRow>
                                 <TableCell><Typography variant='subtitle2' fontWeight={'bold'}>Data Set</Typography></TableCell>
                                 <TableCell>Cases that were opened during the report period</TableCell>
                              </TableRow>
                           </TableBody>
                        </Table>
                     </TableContainer>
                  </Box>

                  <Box className="table-container">
                     <Typography variant="h6" className="subheading">95th percentile of elapsed time</Typography>
                     <TableContainer component={Paper}>
                        <Table>
                           <TableHead className='table-header'>
                              <TableRow>
                                 <TableCell>Case Type / Priority</TableCell>
                                 <TableCell>Response</TableCell>
                                 <TableCell>Workaround</TableCell>
                                 <TableCell>Resolution</TableCell>
                              </TableRow>
                           </TableHead>
                           <TableBody>
                              <TableRow>
                                 <TableCell><Typography fontWeight={'bold'}>Incident - P1</Typography></TableCell>
                                 <DividedCell top={data?.percentileDataList[1].responseTime} bottom='Target: 1 hour'></DividedCell>
                                 <DividedCell top={data?.percentileDataList[1].workaroundTime} bottom='Target: 1 day'></DividedCell>
                                 <DividedCell top={data?.percentileDataList[1].resolutionTime} bottom='Target: 2 days'></DividedCell>
                              </TableRow>
                              <TableRow>
                                 <TableCell><Typography fontWeight={'bold'}>Incident - P2</Typography></TableCell>
                                 <DividedCell top={data?.percentileDataList[2].responseTime} bottom='Target: 4 hour'></DividedCell>
                                 <DividedCell top={data?.percentileDataList[2].workaroundTime} bottom='Target: 2 days'></DividedCell>
                                 <DividedCell top={data?.percentileDataList[2].resolutionTime} bottom='Target: 3 days'></DividedCell>
                              </TableRow>
                              <TableRow>
                                 <TableCell><Typography fontWeight={'bold'}>Incident - P3</Typography></TableCell>
                                 <DividedCell top={data?.percentileDataList[3].responseTime} bottom='Target: 6 hours'></DividedCell>
                                 <DividedCell top={data?.percentileDataList[3].workaroundTime} bottom='Target: 3 days'></DividedCell>
                                 <DividedCell top={data?.percentileDataList[3].resolutionTime} bottom='Target: 1 week'></DividedCell>
                              </TableRow>
                              <TableRow>
                                 <TableCell><Typography fontWeight={'bold'}>Query</Typography></TableCell>
                                 <DividedCell top={data?.percentileDataList[4].responseTime} bottom='Target: 1 day'></DividedCell>
                                 <TableCell><Typography>Not Applicable</Typography></TableCell>
                                 <TableCell><Typography>Not Applicable</Typography></TableCell>
                              </TableRow>
                           </TableBody>
                        </Table>
                     </TableContainer>
                  </Box>

                  <Box className="table-container">
                     <Typography variant="h6" className="subheading">Cases</Typography>
                     <TableContainer component={Paper}>
                        <Table className="case-table">
                           <TableHead className='table-header' >
                              <TableRow>
                                 <TableCell>WSO2 Case ID</TableCell>
                                 <TableCell>Case Type</TableCell>
                                 <TableCell>Priority</TableCell>
                                 <TableCell>State</TableCell>
                                 <TableCell>Opened</TableCell>
                                 <TableCell>Response</TableCell>
                                 <TableCell>Workaround</TableCell>
                                 <TableCell>Resolution</TableCell>
                              </TableRow>
                           </TableHead>
                           <TableBody>
                              {data?.caseDataList && data?.caseDataList.length === 0 ? (
                                 <TableRow>
                                    <TableCell colSpan={colNameArray.length} align="center">
                                       No data available.
                                    </TableCell>
                                 </TableRow>
                              ) :
                                 data?.caseDataList && data?.caseDataList.map((dataRow: Types.DataStruct, index: number) => (
                                    <TableRow key={index} onClick={() => handleRowClick(dataRow)}
                                       sx={{
                                          cursor: "pointer",
                                          '&:hover': {
                                             backgroundColor: '#97c9ff',
                                          }
                                       }}
                                    >
                                       {colAttributeArray.map((attributeName: string, index: number) => (
                                          <TableCell key={index} className="t-col-size">
                                             {dataRow[attributeName]}
                                          </TableCell>
                                       ))}
                                    </TableRow>
                                 ))}
                           </TableBody>
                        </Table>
                     </TableContainer>
                  </Box>
               </Box>
            </div>

         </>
      )
   }

   return (
      <>
         {loading ? (
            <>
               <LinearLoading />
               {<Box className="blur">{MainView()}</Box>}
            </>
         ) : error ? (
            error.status === 404 ? (
               <ItemNotFoundComponent />
            ) : (
               <ErrorComponent />
            )
         ) : (
            <>
               {showReport ? MainView() : FormView()}
               {!showReport && <Box className="blur">{MainView()}</Box>}
            </>
         )}
      </>
   );
};

export default SLAReport;
